import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, LogOut, Menu, X } from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { SidebarNav } from './Sidebar';
import { Button } from '@/components/ui/Button';
import { changeLanguage, currentLanguage, LANGUAGES } from '@/i18n';
import { cn } from '@/lib/utils';

export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isLanguageOpen, setLanguageOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header. `no-print` keeps the whole chrome out of a printed report. */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white no-print">
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            type="button"
            onClick={() => setSidebarOpen((open) => !open)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            aria-label="Menu"
          >
            {isSidebarOpen ? <Menu className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/tharanga-logo.png"
              alt={t('app.name')}
              className="h-8 w-8 shrink-0 rounded-lg object-contain"
            />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-semibold text-slate-900">{t('app.name')}</p>
              <p className="truncate text-xs text-slate-500">{t('app.subtitle')}</p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1">
            {/* Language switcher — the whole point of shipping both locales. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setLanguageOpen((open) => !open)}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-slate-600 hover:bg-slate-100"
                aria-label={t('nav.language')}
              >
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {LANGUAGES.find((language) => language.code === currentLanguage())?.nativeLabel}
                </span>
              </button>

              {isLanguageOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLanguageOpen(false)} aria-hidden />
                  <div className="absolute right-0 z-20 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                    {LANGUAGES.map((language) => (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => {
                          changeLanguage(language.code);
                          setLanguageOpen(false);
                        }}
                        className={cn(
                          'block w-full px-3 py-2 text-left text-sm hover:bg-slate-50',
                          language.code === currentLanguage()
                            ? 'font-medium text-brand-700'
                            : 'text-slate-700',
                        )}
                      >
                        {language.nativeLabel}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-slate-900">{user?.fullName}</p>
              <p className="text-xs text-slate-500">
                {user?.role ? t(`user.roles.${user.role}` as never) : ''}
              </p>
            </div>

            <Button variant="ghost" size="sm" onClick={handleLogout} aria-label={t('nav.logout')}>
              <LogOut className="h-4 w-4" />
              <span className="hidden md:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 border-r border-slate-200 bg-white lg:block no-print">
          <SidebarNav />
        </aside>

        {/* Mobile drawer */}
        {isSidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden no-print">
            <div
              className="absolute inset-0 bg-slate-900/40"
              onClick={() => setSidebarOpen(false)}
              aria-hidden
            />
            <aside className="relative h-full w-64 bg-white shadow-xl">
              <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
                <span className="text-sm font-semibold text-slate-900">{t('app.name')}</span>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <SidebarNav onNavigate={() => setSidebarOpen(false)} />
            </aside>
          </div>
        )}

        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-500 no-print">
        © Adeona Technologies. All Rights Reserved
      </footer>
    </div>
  );
}
